import PropTypes from 'prop-types';
import BackgroundDynamic from '@activebrands/core-web/components/BackgroundDynamic';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import OverlayTrigger from '@activebrands/core-web/libs/OverlayTrigger';
import RichText from '@activebrands/core-web/libs/storyblok/RichText';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import getTextColorStyles from '@activebrands/core-web/utils/style-functions/get-text-color-styles';
import StateButton from 'components/buttons/StateButton';
import Heading from 'components/text/Heading';

const textBreakStyles = { minWidth: 0, overflowWrap: 'break-word' };

const ShopTheLook = ({
    background,
    backgroundOverlay,
    buttonLabel = '',
    buttonTheme = '',
    heading,
    headingFontKeys = ['Primary/16_120_-1', null, 'Primary/16_120_-1', null, 'Primary/16_120_-1'],
    id,
    optiId,
    paragraph = '',
    products = [],
    sbEditable,
    textColor = 'var(--color-palette-off-black)',
}) => {
    const [css] = useStyletron();
    const textColorStyle = getTextColorStyles(textColor);

    return (
        <div
            className={css({ position: 'relative', width: '100%', height: '100%', ...textColorStyle })}
            id={id}
            data-color-scheme="light"
            data-selector="global.module.shop-the-look"
            data-opti-id={optiId}
            {...sbEditable}
        >
            <BackgroundDynamic
                selector="global.module.shop-the-look.media"
                background={background}
                backgroundOverlay={backgroundOverlay}
                loading="lazy"
            />
            <div
                className={css({
                    ...textBreakStyles,
                    height: '100%',
                    display: 'flex',
                    flexDirection: ['column', null, 'row'],
                    padding: ['8px 8px 16px 8px', null, '12px 12px 24px 12px', null, null, '16px 16px 32px 16px'],
                })}
            >
                <div
                    className={css({ marginTop: [null, null, 'auto'], marginRight: [null, null, '32px'] })}
                    data-selector="global.module.shop-the-look.text"
                >
                    {heading?.text && (
                        <Heading
                            as={heading.type}
                            fontKeys={headingFontKeys}
                            data-selector="global.module.shop-the-look.text.title"
                            {...heading.sbEditable}
                        >
                            {heading.text}
                        </Heading>
                    )}
                    {paragraph?.text && (
                        <div
                            className={css({ marginTop: '8px' })}
                            data-selector="global.module.shop-the-look.text.body"
                            {...paragraph.sbEditable}
                        >
                            <RichText data={paragraph.text} />
                        </div>
                    )}
                </div>
                {buttonLabel && products.length > 0 && (
                    <OverlayTrigger overlayId={OVERLAY_ID.SHOP_THE_LOOK} overlayProps={{ products }}>
                        <StateButton
                            data-selector="global.module.shop-the-look.cta"
                            $style={{ marginLeft: 'auto', marginTop: 'auto' }}
                            theme={buttonTheme}
                        >
                            {buttonLabel}
                        </StateButton>
                    </OverlayTrigger>
                )}
            </div>
        </div>
    );
};

ShopTheLook.propTypes = {
    background: PropTypes.arrayOf(PropTypes.object),
    backgroundOverlay: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    buttonLabel: PropTypes.string,
    buttonTheme: PropTypes.string,
    heading: PropTypes.object,
    headingFontKeys: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    id: PropTypes.string.isRequired,
    optiId: PropTypes.string,
    paragraph: PropTypes.object,
    products: PropTypes.array,
    sbEditable: PropTypes.object,
    textColor: PropTypes.string,
};

export default ShopTheLook;
